import { useEffect, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAdminAuth } from '../../hooks/useAdminAuth';
import { navigatorTuningApi } from '../services/navigatorTuningApi';
import type { ConfigVersion, ImpactSample, PlanListFilters, RankingConfigParams } from '../types/navigator-tuning.type';

export const navigatorTuningKeys = {
  all: ['admin', 'navigator-tuning'] as const,
  configVersions: () => [...navigatorTuningKeys.all, 'config-versions'] as const,
  live: () => [...navigatorTuningKeys.configVersions(), 'live'] as const,
  draft: () => [...navigatorTuningKeys.configVersions(), 'draft'] as const,
  plans: (filters: PlanListFilters) => [...navigatorTuningKeys.all, 'plans', filters] as const,
  plan: (sessionId: string) => [...navigatorTuningKeys.all, 'plan', sessionId] as const,
  sandbox: (sessionId: string, params: RankingConfigParams | null) =>
    [...navigatorTuningKeys.plan(sessionId), 'sandbox', params] as const,
  impact: (sample: ImpactSample, params: RankingConfigParams | null) =>
    [...navigatorTuningKeys.all, 'impact', sample, params] as const,
};

/** `value` once it stopped changing for `delayMs`. */
export function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

export function usePlanDetail(sessionId: string) {
  return useQuery({ queryKey: navigatorTuningKeys.plan(sessionId), queryFn: () => navigatorTuningApi.getPlan(sessionId) });
}

/** Replay with `params` (disabled while null). Keeps the previous result while recalculating. */
export function useSandbox(sessionId: string, params: RankingConfigParams | null) {
  return useQuery({
    queryKey: navigatorTuningKeys.sandbox(sessionId, params),
    queryFn: () => navigatorTuningApi.runSandbox(sessionId, params!),
    enabled: params !== null,
    placeholderData: keepPreviousData,
  });
}

/** Impact of `params` on the sample (disabled while null). Keeps the previous result while re-running. */
export function useImpact(params: RankingConfigParams | null, sample: ImpactSample) {
  return useQuery({
    queryKey: navigatorTuningKeys.impact(sample, params),
    queryFn: () => navigatorTuningApi.runImpact(params!, sample),
    enabled: params !== null,
    placeholderData: keepPreviousData,
  });
}

/** Publish/rollback permission (Kinde `admin:tune-navigator`). */
export function useCanTuneNavigator(): boolean {
  const { permissions } = useAdminAuth();
  return permissions.includes('admin:tune-navigator');
}

export function usePlanList(filters: PlanListFilters) {
  return useQuery({
    queryKey: navigatorTuningKeys.plans(filters),
    queryFn: () => navigatorTuningApi.listPlans(filters),
    placeholderData: keepPreviousData,
  });
}

export function useConfigVersions() {
  return useQuery({ queryKey: navigatorTuningKeys.configVersions(), queryFn: navigatorTuningApi.listConfigVersions });
}

export function useLiveConfigVersion() {
  return useQuery({ queryKey: navigatorTuningKeys.live(), queryFn: navigatorTuningApi.getLiveConfigVersion });
}

/** `data` is null when there is no draft. */
export function useDraftConfigVersion() {
  return useQuery({ queryKey: navigatorTuningKeys.draft(), queryFn: navigatorTuningApi.getDraftConfigVersion });
}

/**
 * Creates the draft (no `draft` given) or saves it with its revision. On a 409
 * (isStaleDraftError) the caller should warn and reload the draft.
 */
export function useSaveDraft() {
  const queryClient = useQueryClient();
  return useMutation<ConfigVersion, unknown, { params: RankingConfigParams; draft?: Pick<ConfigVersion, 'id' | 'revision'> }>({
    mutationFn: ({ params, draft }) =>
      draft ? navigatorTuningApi.saveDraft(draft.id, params, draft.revision) : navigatorTuningApi.createDraft(params),
    onSuccess: (saved) => queryClient.setQueryData(navigatorTuningKeys.draft(), saved),
    onSettled: () => queryClient.invalidateQueries({ queryKey: navigatorTuningKeys.configVersions() }),
  });
}

export function useDiscardDraft() {
  const queryClient = useQueryClient();
  return useMutation<void, unknown, string>({
    mutationFn: navigatorTuningApi.discardDraft,
    onSettled: () => queryClient.invalidateQueries({ queryKey: navigatorTuningKeys.configVersions() }),
  });
}

export function usePublishDraft() {
  const queryClient = useQueryClient();
  return useMutation<ConfigVersion, unknown, { id: string; revision: number; justification: string }>({
    mutationFn: ({ id, revision, justification }) => navigatorTuningApi.publishDraft(id, revision, justification),
    onSettled: () => queryClient.invalidateQueries({ queryKey: navigatorTuningKeys.configVersions() }),
  });
}

export function useRollbackConfigVersion() {
  const queryClient = useQueryClient();
  return useMutation<ConfigVersion, unknown, { id: string; justification: string }>({
    mutationFn: ({ id, justification }) => navigatorTuningApi.rollbackToVersion(id, justification),
    onSettled: () => queryClient.invalidateQueries({ queryKey: navigatorTuningKeys.configVersions() }),
  });
}
